import axios from 'axios'
import store from 'state/store/store'
import { setAuth, setUser } from 'state/actions'

const EXCLUDED_URLS = [
    '/auth',
    '/marketplace/api/refresh_tokens',
    '/check_auth'
]

const createHttpClient = (baseURL) => {
    const instance = axios.create({
        baseURL,
        responseType: 'json',
        timeout: 200000,
        withCredentials: true,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept-Language': store.getState().lang,
            'Content-Type': 'application/json'
        }
    })

    /**
     * ===============================
     * REQUEST INTERCEPTOR
     * ===============================
     */
    instance.interceptors.request.use(
        (config) => {
            config.headers['Accept-Language'] = store.getState().lang
            return config
        },
        (error) => Promise.reject(error)
    )

    /**
     * ===============================
     * RESPONSE INTERCEPTOR (401 refresh)
     * ===============================
     */
    let isRefreshing = false
    let failedQueue = []

    const processQueue = (error) => {
        failedQueue.forEach(p => {
            if (error) p.reject(error)
            else p.resolve()
        })
        failedQueue = []
    }

    instance.interceptors.response.use(
        response => response,
        async (error) => {

            const originalRequest = error.config
            const status = error.response?.status
            const requestUrl = originalRequest?.url || ''
            const normalizedUrl = requestUrl.startsWith('/') ? requestUrl : `/${requestUrl}`;
            const isExcluded = EXCLUDED_URLS.some(url =>
                normalizedUrl.startsWith(url)
            )

            // если не 401 или исключён URL → просто reject
            if (status !== 401 || isExcluded) {
                return Promise.reject(error)
            }

            // защита от повторного retry
            if (originalRequest._retry) {
                // повторный 401 → logout: сброс state и редирект
                store.dispatch(setUser(null))
                store.dispatch(setAuth(false))

                const isAuthPage = window.location.pathname.startsWith('/auth')
                if (!isAuthPage) {
                    window.location.href = '/auth?previous=0'
                }

                return Promise.reject(error)
            }

            originalRequest._retry = true

            // очередь для параллельных запросов
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                    .then(() => instance(originalRequest))
                    .catch(err => Promise.reject(err))
            }

            isRefreshing = true

            try {
                // попытка обновить токен
                await axios.post('/marketplace/api/refresh_tokens', {}, { withCredentials: true })

                // все ожидающие запросы повторяем
                processQueue(null)

                return instance(originalRequest)

            } catch (refreshError) {
                // refresh не удался → logout
                processQueue(refreshError)
                store.dispatch(setUser(null))
                store.dispatch(setAuth(false))

                const isAuthPage = window.location.pathname.startsWith('/auth')
                if (!isAuthPage) {
                    window.location.href = '/auth?previous=0'
                }

                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }
    )

    return instance
}

export default createHttpClient